import React, { useState, useEffect } from 'react';
import { Search, Filter, Sparkles, ChevronRight, X, RefreshCw, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/RfpButton';
import { Badge } from '../ui/RfpBadge';
import { authedFetch } from '@/lib/authedFetch';

const apibase = process.env.REACT_APP_API_BASE_URL;

export const GovernmentOpportunities = () => {
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [crawling, setCrawling] = useState(false);
    const [selectedOpp, setSelectedOpp] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchLiveOpportunities = async () => {
        setLoading(true);
        try {
            const res = await authedFetch(`${apibase}/opportunities`, {
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            const json = await res.json();
            if (json?.success) {
                setOpportunities(json.data);
            }
        } catch (err) {
            console.error('Failed to fetch opportunities:', err);
        } finally {
            setLoading(false);
        }
    };

    const [masterCrawling, setMasterCrawling] = useState(false);

    const handleMasterParallelFetch = async () => {
        setMasterCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/master-parallel-fetch`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('Master Parallel Fetch failed:', err);
        } finally {
            setMasterCrawling(false);
        }
    };

    const [usItCrawling, setUsItCrawling] = useState(false);

    const handleScrapeUSITPortals = async () => {
        setUsItCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/scrape-us-it-portals`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('US IT Portals Scrape failed:', err);
        } finally {
            setUsItCrawling(false);
        }
    };

    const [cityCrawling, setCityCrawling] = useState(false);

    const handleScrapeCitiesCounties = async () => {
        setCityCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/scrape-us-cities-counties`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('US City & County Scrape failed:', err);
        } finally {
            setCityCrawling(false);
        }
    };

    const [megaCrawling, setMegaCrawling] = useState(false);

    const handleScrape50States = async () => {
        setMegaCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/scrape-50-states`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('50-State Mega Scrape failed:', err);
        } finally {
            setMegaCrawling(false);
        }
    };

    const [sbirCrawling, setSbirCrawling] = useState(false);

    const handleScrapeSBIR = async () => {
        setSbirCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/scrape-sbir-sttr`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('SBIR R&D Scrape failed:', err);
        } finally {
            setSbirCrawling(false);
        }
    };

    const [erateCrawling, setErateCrawling] = useState(false);

    const handleScrapeErate = async () => {
        setErateCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/scrape-usac-erate`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('USAC E-Rate Scrape failed:', err);
        } finally {
            setErateCrawling(false);
        }
    };

    const [usaspendingCrawling, setUsaspendingCrawling] = useState(false);

    const handleScrapeUSASpending = async () => {
        setUsaspendingCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/scrape-usaspending-awards`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('USASpending Scrape failed:', err);
        } finally {
            setUsaspendingCrawling(false);
        }
    };

    const handleTriggerCrawl = async () => {
        setCrawling(true);
        try {
            await authedFetch(`${apibase}/opportunities/trigger-crawl`, {
                method: 'POST',
                headers: { 'ngrok-skip-browser-warning': 'true' },
            });
            await fetchLiveOpportunities();
        } catch (err) {
            console.error('Crawler trigger failed:', err);
        } finally {
            setCrawling(false);
        }
    };

    // AI natural-language search
    const [nlQuery, setNlQuery] = useState('');
    const [nlLoading, setNlLoading] = useState(false);
    const [nlResult, setNlResult] = useState(null);

    const handleNaturalLanguageSearch = async () => {
        if (!nlQuery.trim()) return;
        setNlLoading(true);
        try {
            const res = await authedFetch(`${apibase}/search/natural-language`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
                body: JSON.stringify({ query: nlQuery }),
            });
            const json = await res.json();
            if (json?.success) {
                setNlResult(json.data);
            }
        } catch (err) {
            console.error('Natural language search failed:', err);
        } finally {
            setNlLoading(false);
        }
    };

    const clearNaturalLanguageSearch = () => {
        setNlQuery('');
        setNlResult(null);
    };

    // AI proposal generation
    const [generatingProposal, setGeneratingProposal] = useState(false);
    const [proposal, setProposal] = useState(null);

    const handleGenerateProposal = async (opp) => {
        setGeneratingProposal(true);
        setProposal(null);
        try {
            const res = await authedFetch(`${apibase}/proposal/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
                body: JSON.stringify({ opportunity_id: opp.id, solicitation_number: opp.solicitation_number }),
            });
            const json = await res.json();
            if (json?.success) {
                setProposal(json.data.proposal);
            }
        } catch (err) {
            console.error('Proposal generation failed:', err);
        } finally {
            setGeneratingProposal(false);
        }
    };

    useEffect(() => {
        fetchLiveOpportunities();
    }, []);

    const [selectedState, setSelectedState] = useState('ALL');
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const [selectedSourceType, setSelectedSourceType] = useState('ALL');

    const filteredOpps = opportunities.filter(opp => {
        // Scraped opportunities don't always have every field populated
        // (source_name/agency/title/solicitation_number can come back
        // undefined depending on the source portal) - `.toLowerCase()` on
        // undefined throws, which is what broke the state filter (it's the
        // one that touches source_name unconditionally). Default every
        // field to '' before lowercasing so a missing value just fails to
        // match instead of crashing the whole filter.
        const title = (opp.title || '').toLowerCase();
        const agency = (opp.agency || '').toLowerCase();
        const solicitationNumber = (opp.solicitation_number || '').toLowerCase();
        const sourceName = (opp.source_name || '').toLowerCase();
        const search = searchQuery.toLowerCase();

        const matchesSearch =
            title.includes(search) ||
            agency.includes(search) ||
            solicitationNumber.includes(search);
        const matchesState = selectedState === 'ALL' ||
            solicitationNumber.includes(`-${selectedState.toLowerCase()}-`) ||
            title.includes(`(${selectedState.toLowerCase()})`) ||
            sourceName.includes(`state of ${selectedState.toLowerCase()}`);
        const matchesCategory = selectedCategory === 'ALL' ||
            title.includes(selectedCategory.toLowerCase()) ||
            (opp.tech_stack || []).some((t) => (t || '').toLowerCase().includes(selectedCategory.toLowerCase()));
        const matchesSource = selectedSourceType === 'ALL' ||
            sourceName.includes(selectedSourceType.toLowerCase());
        return matchesSearch && matchesState && matchesCategory && matchesSource;
    });

    const US_STATES_LIST = [
        "ALL", "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN",
        "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
        "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT",
        "VT", "VA", "WA", "WV", "WI", "WY"
    ];

    return (
        <div className="p-6 space-y-6">
            {/* Header & Control Toolbar */}
            <div className="glass-card p-6 rounded-2xl border border-indigo-500/20 bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950/40 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-2xl font-bold text-white font-sans">US Procurement Intelligence Engine</h2>
                            <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 text-xs font-medium">
                                100% Free Public US Scrapers Active
                            </span>
                        </div>
                        <p className="text-xs text-slate-400">
                            Aggregating live RFPs across SAM.gov, SBIR/STTR AI R&D, USAC E-Rate K-12 School IT, USASpending Awards, 50 States & Cities.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchLiveOpportunities} disabled={loading} className="shrink-0">
                            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                        </Button>
                        <Button variant="primary" size="sm" className="bg-gradient-to-r from-emerald-600 via-indigo-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-bold shadow-lg shrink-0" onClick={handleMasterParallelFetch} disabled={masterCrawling}>
                            <Sparkles className="w-4 h-4 mr-1.5" />
                            {masterCrawling ? 'Parallel Fetching All...' : '🚀 Master Parallel Scrape (All Sources)'}
                        </Button>
                    </div>
                </div>

                {/* Scraper Action Buttons Toolbar */}
                <div className="pt-3 border-t border-slate-800/80 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-slate-500 text-xs font-medium mr-1">Trigger Scrapers:</span>
                    <button
                        onClick={handleScrapeSBIR}
                        disabled={sbirCrawling}
                        className="px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 font-medium transition-colors flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                        {sbirCrawling ? 'Fetching SBIR...' : 'SBIR/STTR AI R&D ($4B+)'}
                    </button>
                    <button
                        onClick={handleScrapeErate}
                        disabled={erateCrawling}
                        className="px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-medium transition-colors flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                        {erateCrawling ? 'Fetching E-Rate...' : 'USAC K-12 School IT ($4.5B+)'}
                    </button>
                    <button
                        onClick={handleScrapeUSASpending}
                        disabled={usaspendingCrawling}
                        className="px-3 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-medium transition-colors flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                        {usaspendingCrawling ? 'Fetching Awards...' : 'USASpending Benchmarks'}
                    </button>
                    <button
                        onClick={handleScrape50States}
                        disabled={megaCrawling}
                        className="px-3 py-1.5 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-medium transition-colors flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                        {megaCrawling ? 'Fetching States...' : '50 US States'}
                    </button>
                    <button
                        onClick={handleTriggerCrawl}
                        disabled={crawling}
                        className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-colors flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-slate-400" />
                        {crawling ? 'Fetching SAM...' : 'SAM.gov Federal'}
                    </button>
                    <button
                        onClick={handleScrapeUSITPortals}
                        disabled={usItCrawling}
                        className="px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 font-medium transition-colors flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-rose-400" />
                        {usItCrawling ? 'Fetching Portals...' : 'US IT Portals'}
                    </button>
                    <button
                        onClick={handleScrapeCitiesCounties}
                        disabled={cityCrawling}
                        className="px-3 py-1.5 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-300 border border-teal-500/30 font-medium transition-colors flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-teal-400" />
                        {cityCrawling ? 'Fetching Cities...' : 'US Cities & Counties'}
                    </button>
                </div>
            </div>

            {/* AI Natural-Language Search */}
            {/* <div className="glass-card p-4 rounded-xl border border-purple-500/20 space-y-3">
                <div className="flex items-center gap-2.5 bg-slate-900/90 border border-purple-500/30 rounded-lg px-3 py-2">
                    <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
                    <input
                        type="text"
                        value={nlQuery}
                        onChange={(e) => setNlQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleNaturalLanguageSearch()}
                        placeholder='Ask AI, e.g. "AI opportunities over $300k using Azure"'
                        className="bg-transparent text-xs text-slate-200 focus:outline-none w-full placeholder-slate-500"
                    />
                    <Button variant="primary" size="sm" onClick={handleNaturalLanguageSearch} disabled={nlLoading || !nlQuery.trim()} className="shrink-0">
                        {nlLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Ask AI'}
                    </Button>
                    {nlResult && (
                        <button onClick={clearNaturalLanguageSearch} className="p-1 text-slate-400 hover:text-white shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {nlResult && (
                    <div className="space-y-3 pt-1">
                        {nlResult.ai_answer && (
                            <p className="text-xs text-purple-200 bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 leading-relaxed">
                                {nlResult.ai_answer}
                            </p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {(nlResult.matches || []).map((m) => (
                                <div key={m.id} className="p-3 rounded-lg bg-slate-900/80 border border-slate-800 space-y-1">
                                    <p className="text-xs font-semibold text-white line-clamp-2">{m.title}</p>
                                    <p className="text-[11px] text-slate-400">{m.agency}</p>
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-emerald-400 font-semibold">{m.budget}</span>
                                        <span className="text-indigo-400">Score {m.score}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {(nlResult.matches || []).length === 0 && (
                            <p className="text-xs text-slate-500">No matching opportunities found for that query.</p>
                        )}
                    </div>
                )}
            </div> */}

            {/* Network Stats Cards Banner */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="glass-card p-4 rounded-xl border border-slate-800/80 bg-gradient-to-br from-amber-950/20 to-slate-900/60">
                    <p className="text-[11px] text-amber-400 font-semibold uppercase tracking-wider">Defense & AI R&D Hubs</p>
                    <p className="text-xl font-bold text-white mt-1">SBIR / STTR ($4B+)</p>
                    <p className="text-xs text-slate-400 mt-1">DARPA, DoD, NASA, NSF, NIH, DoE Open AI Topics</p>
                </div>

                <div className="glass-card p-4 rounded-xl border border-slate-800/80 bg-gradient-to-br from-emerald-950/20 to-slate-900/60">
                    <p className="text-[11px] text-emerald-400 font-semibold uppercase tracking-wider">K-12 School Infrastructure</p>
                    <p className="text-xl font-bold text-white mt-1">USAC E-Rate ($4.5B+)</p>
                    <p className="text-xs text-slate-400 mt-1">LAUSD, Chicago, Miami-Dade Fiber & Network RFPs</p>
                </div>

                <div className="glass-card p-4 rounded-xl border border-slate-800/80 bg-gradient-to-br from-cyan-950/20 to-slate-900/60">
                    <p className="text-[11px] text-cyan-400 font-semibold uppercase tracking-wider">Federal & Defense</p>
                    <p className="text-xl font-bold text-white mt-1">SAM.gov & USASpending</p>
                    <p className="text-xs text-slate-400 mt-1">Historical Contract Award Benchmarks & Pricing</p>
                </div>

                <div className="glass-card p-4 rounded-xl border border-slate-800/80 bg-gradient-to-br from-indigo-950/20 to-slate-900/60">
                    <p className="text-[11px] text-indigo-400 font-semibold uppercase tracking-wider">State & Municipal Networks</p>
                    <p className="text-xl font-bold text-white mt-1">50 States & Cities</p>
                    <p className="text-xs text-slate-400 mt-1">Cal eProcure, TxSmartBuy, NYSCR, eVA, Cities</p>
                </div>
            </div>

            {/* Structured Filter Bar */}
            <div className="glass-card p-4 rounded-xl space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Keyword Search */}
                    <div className="flex items-center gap-2.5 bg-slate-900/90 border border-slate-800 rounded-lg px-3 py-2">
                        <Search className="w-4 h-4 text-slate-400 shrink-0" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search title, solicitation #, agency..."
                            className="bg-transparent text-xs text-slate-200 focus:outline-none w-full placeholder-slate-500"
                        />
                    </div>

                    {/* IT Category Select */}
                    <div className="flex items-center gap-2 bg-slate-900/90 border border-slate-800 rounded-lg px-3 py-2">
                        <Filter className="w-4 h-4 text-cyan-400 shrink-0" />
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="bg-transparent text-xs text-cyan-300 font-semibold focus:outline-none cursor-pointer w-full"
                        >
                            <option value="ALL" className="bg-slate-900 text-white">All US IT Categories</option>
                            <option value="Web" className="bg-slate-900 text-white">Web & Mobile Development</option>
                            <option value="ERP" className="bg-slate-900 text-white">Enterprise ERP (SAP/Oracle)</option>
                            <option value="AI" className="bg-slate-900 text-white">AI, LLM & Data Analytics</option>
                            <option value="Cloud" className="bg-slate-900 text-white">Cloud & Cybersecurity</option>
                            <option value="Networking" className="bg-slate-900 text-white">School Infrastructure & Networking</option>
                        </select>
                    </div>

                    {/* Source Network Select */}
                    <div className="flex items-center gap-2 bg-slate-900/90 border border-slate-800 rounded-lg px-3 py-2">
                        <Filter className="w-4 h-4 text-purple-400 shrink-0" />
                        <select
                            value={selectedSourceType}
                            onChange={(e) => setSelectedSourceType(e.target.value)}
                            className="bg-transparent text-xs text-purple-300 font-semibold focus:outline-none cursor-pointer w-full"
                        >
                            <option value="ALL" className="bg-slate-900 text-white">All Networks</option>
                            <option value="SAM.gov" className="bg-slate-900 text-white">SAM.gov Federal</option>
                            <option value="SBIR" className="bg-slate-900 text-white">SBIR.gov AI R&D</option>
                            <option value="USAC" className="bg-slate-900 text-white">USAC E-Rate K-12</option>
                            <option value="USASpending" className="bg-slate-900 text-white">USASpending Awards</option>
                            <option value="State" className="bg-slate-900 text-white">50 US State eProcurement</option>
                        </select>
                    </div>

                    {/* US State Select */}
                    <div className="flex items-center gap-2 bg-slate-900/90 border border-slate-800 rounded-lg px-3 py-2">
                        <Filter className="w-4 h-4 text-indigo-400 shrink-0" />
                        <select
                            value={selectedState}
                            onChange={(e) => setSelectedState(e.target.value)}
                            className="bg-transparent text-xs text-indigo-300 font-semibold focus:outline-none cursor-pointer w-full"
                        >
                            {US_STATES_LIST.map((st) => (
                                <option key={st} value={st} className="bg-slate-900 text-white">
                                    {st === "ALL" ? "All 50 US States & Federal" : `State: ${st}`}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex justify-between items-center text-xs text-slate-400 pt-1">
                    <span>Found <strong>{filteredOpps.length}</strong> matching live procurement opportunities</span>
                    {(selectedState !== 'ALL' || selectedCategory !== 'ALL' || selectedSourceType !== 'ALL' || searchQuery) && (
                        <button
                            onClick={() => { setSelectedState('ALL'); setSelectedCategory('ALL'); setSelectedSourceType('ALL'); setSearchQuery(''); }}
                            className="text-indigo-400 hover:underline text-xs"
                        >
                            Reset Filters
                        </button>
                    )}
                </div>
            </div>

            {/* Opportunities Table */}
            <div className="glass-card rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-slate-400">
                        <RefreshCw className="w-8 h-8 mx-auto animate-spin text-indigo-400 mb-2" />
                        <p>Loading live procurement opportunities from database engine...</p>
                    </div>
                ) : (
                    <table className="w-full text-left text-sm text-slate-300">
                        <thead className="bg-slate-900/90 text-xs uppercase text-slate-400 border-b border-slate-800">
                            <tr>
                                <th className="px-5 py-4">Solicitation & Title</th>
                                <th className="px-5 py-4">Agency</th>
                                <th className="px-5 py-4">Budget</th>
                                <th className="px-5 py-4">Tech Stack</th>
                                <th className="px-5 py-4">Match Score</th>
                                <th className="px-5 py-4">Decision</th>
                                <th className="px-5 py-4">Source Link</th>
                                <th className="px-5 py-4">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                            {filteredOpps.map((opp) => (
                                <tr key={opp.id} className="hover:bg-slate-800/40 transition-colors">
                                    <td className="px-5 py-4">
                                        <p className="font-semibold text-white hover:text-indigo-400 cursor-pointer" onClick={() => setSelectedOpp(opp)}>
                                            {opp.title}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-slate-500 font-mono">{opp.solicitation_number}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-slate-300">{opp.agency}</td>
                                    <td className="px-5 py-4 font-semibold text-emerald-400">
                                        ${(opp.estimated_budget || 0).toLocaleString()}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {(opp.tech_stack || []).map((t) => (
                                                <span key={t} className="px-2 py-0.5 rounded bg-slate-800 text-[11px] text-slate-300 border border-slate-700">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className="font-bold text-indigo-400 bg-indigo-500/10 px-2 py-1 rounded border border-indigo-500/20">
                                            {opp.match_score} / 100
                                        </span>
                                    </td>
                                    <td className="px-5 py-4">
                                        <Badge variant={(opp.recommendation || '').toLowerCase()}>
                                            {opp.recommendation}
                                        </Badge>
                                    </td>
                                    <td className="px-5 py-4">
                                        <a
                                            href={opp.source_url || `https://sam.gov/search/?index=opp&keywords=${opp.solicitation_number}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center text-xs text-cyan-400 hover:text-cyan-300 font-medium hover:underline gap-1 bg-cyan-500/10 px-2.5 py-1 rounded border border-cyan-500/20"
                                        >
                                            {opp.source_name || "Official Source"} <ExternalLink className="w-3 h-3 ml-1" />
                                        </a>
                                    </td>
                                    <td className="px-5 py-4">
                                        <Button variant="outline" size="sm" onClick={() => setSelectedOpp(opp)}>
                                            Inspect <ChevronRight className="w-3.5 h-3.5 ml-1" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Detail Drawer */}
            {selectedOpp && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end">
                    <div className="w-full max-w-xl glass-card h-full p-6 overflow-y-auto space-y-6 border-l border-slate-800">
                        <div className="flex justify-between items-center border-b border-slate-800 pb-4">
                            <div>
                                <span className="text-xs font-mono text-indigo-400">{selectedOpp.solicitation_number}</span>
                                <h3 className="text-lg font-bold text-white">{selectedOpp.title}</h3>
                            </div>
                            <button onClick={() => { setSelectedOpp(null); setProposal(null); }} className="p-2 text-slate-400 hover:text-white rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-xs">
                            <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                                <p className="text-slate-400">Match Score</p>
                                <p className="text-lg font-bold text-indigo-400">{selectedOpp.match_score} / 100</p>
                            </div>
                            <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                                <p className="text-slate-400">Estimated Win Probability</p>
                                <p className="text-lg font-bold text-emerald-400">{selectedOpp.win_probability}</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <h4 className="font-semibold text-sm text-white">Extracted AI Metadata & Reasoning</h4>
                            <p className="text-xs text-slate-300 bg-slate-900/80 p-3 rounded-lg border border-slate-800 leading-relaxed">
                                {selectedOpp.decision_reasoning}
                            </p>
                            <div className="space-y-2 text-xs text-slate-300">
                                <p><strong className="text-slate-400">Procurement Source:</strong> {selectedOpp.source_name || "US Government Portal"}</p>
                                <p><strong className="text-slate-400">Agency:</strong> {selectedOpp.agency}</p>
                                <p><strong className="text-slate-400">Budget:</strong> ${selectedOpp.estimated_budget?.toLocaleString()}</p>
                                <p><strong className="text-slate-400">Deadline:</strong> {selectedOpp.deadline_date}</p>
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-3">
                            <h4 className="font-semibold text-xs text-indigo-300 uppercase tracking-wider">US Compliance & Security Analysis</h4>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="p-2.5 rounded-lg bg-slate-800/60 border border-slate-700/50">
                                    <span className="text-slate-400 block text-[10px]">FAR / DFARS Standard</span>
                                    <span className="text-cyan-300 font-mono font-bold">FAR 52.204-21 / DFARS 252</span>
                                </div>
                                <div className="p-2.5 rounded-lg bg-slate-800/60 border border-slate-700/50">
                                    <span className="text-slate-400 block text-[10px]">FedRAMP / NIST SP 800-53</span>
                                    <span className="text-emerald-300 font-bold">Moderate / NIST 800-171</span>
                                </div>
                                <div className="p-2.5 rounded-lg bg-slate-800/60 border border-slate-700/50">
                                    <span className="text-slate-400 block text-[10px]">Required Clearance Level</span>
                                    <span className="text-purple-300 font-bold">Secret / Public Trust</span>
                                </div>
                                <div className="p-2.5 rounded-lg bg-slate-800/60 border border-slate-700/50">
                                    <span className="text-slate-400 block text-[10px]">US Set-Aside Program</span>
                                    <span className="text-amber-300 font-bold">Small Business / 8(a) Eligible</span>
                                </div>
                            </div>
                        </div>

                        {/* AI Proposal Generation */}
                        <div className="p-4 rounded-xl bg-slate-900/90 border border-indigo-500/20 space-y-3">
                            <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-xs text-indigo-300 uppercase tracking-wider flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5" /> AI-Generated Proposal
                                </h4>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleGenerateProposal(selectedOpp)}
                                    disabled={generatingProposal}
                                >
                                    {generatingProposal ? (
                                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</>
                                    ) : (
                                        <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate Proposal</>
                                    )}
                                </Button>
                            </div>
                            {proposal && (
                                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                    {Object.entries(proposal).map(([section, content]) => (
                                        <div key={section} className="p-2.5 rounded-lg bg-slate-800/60 border border-slate-700/50">
                                            <p className="text-[11px] font-semibold text-cyan-300 uppercase tracking-wider mb-1">
                                                {section.replace(/_/g, ' ')}
                                            </p>
                                            <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">
                                                {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="pt-4 border-t border-slate-800 flex flex-col gap-3">
                            <a
                                href={selectedOpp.source_url || `https://sam.gov/search/?index=opp&keywords=${selectedOpp.solicitation_number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full"
                            >
                                <Button variant="outline" className="w-full text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10">
                                    Verify Portal ({selectedOpp.source_name || "Official Source"}) <ExternalLink className="w-4 h-4 ml-2" />
                                </Button>
                            </a>
                            <Button
                                variant="primary"
                                className="w-full"
                                onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = selectedOpp.source_url || `https://sam.gov/search/?index=opp&keywords=${selectedOpp.solicitation_number}`;
                                    window.open(link.href, '_blank');
                                }}
                            >
                                Inspect Solicits on SAM.gov
                            </Button>
                            <Button variant="secondary" className="w-full" onClick={() => { setSelectedOpp(null); setProposal(null); }}>Close</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};